import React, { useState } from 'react';
import { useMatchStore } from '../data/matchStore';
import { allPartiesIncludingNonParliamentary as PARTIES } from '../data/parties';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Plus, Trash2, HeartHandshake, Settings, BarChart2, CheckCircle, XCircle, Layout, Pencil, Save, Check } from 'lucide-react';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BulkImportDialog } from '../components/BulkImportDialog';
import { getApiUrl } from '../utils/apiConfig';

export default function AdminMatchPage() {
  const { 
    isMatchEnabled, toggleMatchEnabled, itemsPerSession, updateItemsPerSession,
    propositions, deleteProposition, addProposition, updatePropositionStatus, updateProposition,
    bannerConfig, updateBannerConfig,
    analytics, disclaimer, updateDisclaimer, resetAnalytics,
    consolidatedParties
  } = useMatchStore();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'propositions' | 'moderation' | 'config' | 'stats'>('propositions');
  const [showAddForm, setShowAddForm] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);

  // Form state
  const [theme, setTheme] = useState("");
  const [domain, setDomain] = useState("");
  const [textFr, setTextFr] = useState("");
  const [textAr, setTextAr] = useState("");
  const [keywordsStr, setKeywordsStr] = useState("");
  const [selectedParties, setSelectedParties] = useState<string[]>([]);
  
  const [editingPropId, setEditingPropId] = useState<string | null>(null);
  const [editTheme, setEditTheme] = useState("");
  const [editDomain, setEditDomain] = useState("");
  const [editFr, setEditFr] = useState("");
  const [editParties, setEditParties] = useState<string[]>([]);

  const [discFr, setDiscFr] = useState("");
  const [discAr, setDiscAr] = useState("");

  React.useEffect(() => {
    if (disclaimer) {
      setDiscFr(disclaimer.fr || "");
      setDiscAr(disclaimer.ar || "");
    }
  }, [disclaimer]);

  const startEditing = (prop: any) => {
    setEditingPropId(prop.id);
    setEditTheme(prop.theme);
    setEditDomain(prop.domain);
    setEditFr(prop.textFr);
    setEditParties([...prop.partyIds]);
  };

  const saveEdit = (id: string) => {
    updateProposition(id, {
      theme: editTheme,
      domain: editDomain,
      textFr: editFr,
      partyIds: editParties
    });
    setEditingPropId(null);
    toast({ title: "Modifications sauvegardées" });
  };

  const handleBulkImport = async (data: any[]) => {
    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/match.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: 'bulk_add',
          propositions: data.map(p => ({
            theme: p.theme || p.Thème || "",
            domain: p.domain || p.Domaine || "",
            textFr: p.textFr || p.TexteFR || "",
            textAr: p.textAr || p.TexteAR || "",
            keywords: (p.keywords || p["Mots-clés"] || "").split(',').map((k: string) => k.trim()).filter(Boolean),
            partyIds: p.parti ? [p.parti.trim()] : [],
            status: 'valide',
            submittedBy: 'admin_bulk'
          }))
        })
      });
      if (res.ok) {
        toast({ title: "Import Réussi", description: `${data.length} propositions importées.` });
        window.location.reload(); // Quick way to refresh store
      } else {
        toast({ title: "Erreur", description: "L'importation a échoué.", variant: "destructive" });
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Erreur", description: "L'importation a échoué.", variant: "destructive" });
    }
  };

  const handleToggle = (checked: boolean) => {
    toggleMatchEnabled(checked);
    toast({ title: `Module ${checked ? 'activé' : 'désactivé'}` });
  };

  const getPartyName = (id: string) => {
    const party = PARTIES.find(p => p.acronym === id);
    return party ? party.acronym : id;
  };

  const toggleParty = (id: string) => {
    if (selectedParties.includes(id)) {
      setSelectedParties(selectedParties.filter(p => p !== id));
    } else {
      setSelectedParties([...selectedParties, id]);
    }
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!theme || !textFr || selectedParties.length === 0) {
      return toast({ title: "Erreur", description: "Veuillez remplir les champs obligatoires et sélectionner au moins un parti.", variant: "destructive" });
    }
    addProposition({
      theme,
      domain,
      textFr,
      textAr,
      keywords: keywordsStr.split(',').map(k => k.trim()).filter(Boolean),
      partyIds: selectedParties,
      status: 'valide',
      submittedBy: 'admin'
    });
    setShowAddForm(false);
    setTheme(""); setDomain(""); setTextFr(""); setTextAr(""); setKeywordsStr(""); setSelectedParties([]);
    toast({ title: "Proposition ajoutée" });
  };

  const validProps = propositions.filter(p => p.status === 'valide');
  const pendingProps = propositions.filter(p => p.status === 'en_attente');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-heading flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-primary" />
            Match ton Parti
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">Gérez le contenu du test d'affinité politique type Tinder.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted/20 px-3 py-1.5 rounded-lg border border-dashed border-border/60">
          <Settings className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Visibilité : <strong>Centre de Contrôle (⚙️)</strong></span>
        </div>
      </div>

      <div className="flex gap-2 border-b">
        <button onClick={() => setActiveTab('propositions')} className={`px-4 py-2 font-semibold text-sm border-b-2 ${activeTab === 'propositions' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          Propositions ({validProps.length})
        </button>
        <button onClick={() => setActiveTab('moderation')} className={`px-4 py-2 font-semibold text-sm border-b-2 flex items-center gap-2 ${activeTab === 'moderation' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          Modération {pendingProps.length > 0 && <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingProps.length}</span>}
        </button>
        <button onClick={() => setActiveTab('config')} className={`px-4 py-2 font-semibold text-sm border-b-2 flex items-center gap-2 ${activeTab === 'config' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <Layout className="w-4 h-4" /> Design
        </button>
        <button onClick={() => setActiveTab('stats')} className={`px-4 py-2 font-semibold text-sm border-b-2 flex items-center gap-2 ${activeTab === 'stats' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
          <BarChart2 className="w-4 h-4" /> Statistiques
        </button>
      </div>

      {activeTab === 'propositions' && (
        <div className="bg-card rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between bg-muted/20">
            <div>
              <h2 className="font-semibold flex items-center gap-2">Base de propositions actives</h2>
              {consolidatedParties && consolidatedParties.length > 0 && (
                <p className="text-xs text-emerald-600 font-medium mt-1">
                  ✓ Partis ayant validé leurs choix : {consolidatedParties.join(', ')}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => setIsBulkOpen(true)}>
                Import Excel
              </Button>
              <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}>
                <Plus className="w-4 h-4 mr-2" /> Ajouter manuellement
              </Button>
            </div>
          </div>
          
          <BulkImportDialog 
            isOpen={isBulkOpen} 
            onClose={() => setIsBulkOpen(false)} 
            onImport={handleBulkImport} 
            type="propositions" 
          />
          
          {showAddForm && (
            <div className="p-4 border-b bg-muted/10">
              <form onSubmit={handleAddSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Thème (ex: Éducation)</Label>
                    <Input value={theme} onChange={e => setTheme(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <Label>Domaine</Label>
                    <Input value={domain} onChange={e => setDomain(e.target.value)} />
                  </div>
                  <div className="space-y-1">
                    <Label>Texte (FR)</Label>
                    <Input value={textFr} onChange={e => setTextFr(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <Label>Texte (AR)</Label>
                    <Input value={textAr} onChange={e => setTextAr(e.target.value)} dir="rtl" />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label>Mots-clés (séparés par virgule)</Label>
                    <Input value={keywordsStr} onChange={e => setKeywordsStr(e.target.value)} placeholder="Bourse, Étudiants..." />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Partis associés (Ceux qui recevront +1 point pour cette idée)</Label>
                  <div className="flex flex-wrap gap-2">
                    {PARTIES.map(p => (
                      <button type="button" key={p.acronym} onClick={() => toggleParty(p.acronym)} className={`px-2 py-1 text-xs rounded-full border transition-colors ${selectedParties.includes(p.acronym) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground hover:bg-muted'}`}>
                        {p.acronym}
                      </button>
                    ))}
                  </div>
                </div>
                <Button type="submit">Sauvegarder la proposition</Button>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-muted-foreground uppercase text-xs">
                <tr>
                  <th className="px-4 py-3">Thème</th>
                  <th className="px-4 py-3">Texte (FR)</th>
                  <th className="px-4 py-3">Partis (+1 pt)</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {validProps.map(prop => (
                  <tr key={prop.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">
                      {editingPropId === prop.id ? (
                        <Input value={editTheme} onChange={e => setEditTheme(e.target.value)} className="h-8 text-xs w-28" />
                      ) : prop.theme}
                    </td>
                    <td className="px-4 py-3 truncate max-w-xs">
                      {editingPropId === prop.id ? (
                        <Input value={editFr} onChange={e => setEditFr(e.target.value)} className="h-8 text-xs" />
                      ) : prop.textFr}
                    </td>
                    <td className="px-4 py-3">
                      {editingPropId === prop.id ? (
                         <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {PARTIES.map(p => (
                              <button type="button" key={p.acronym} onClick={() => setEditParties(prev => prev.includes(p.acronym) ? prev.filter(x => x !== p.acronym) : [...prev, p.acronym])} className={`px-1.5 py-0.5 text-[10px] rounded border transition-colors ${editParties.includes(p.acronym) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground'}`}>
                                {p.acronym}
                              </button>
                            ))}
                         </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {prop.partyIds.map(id => <span key={id} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">{getPartyName(id)}</span>)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{prop.submittedBy}</td>
                    <td className="px-4 py-3 text-right">
                      {editingPropId === prop.id ? (
                        <div className="flex justify-end gap-1">
                           <Button variant="outline" size="sm" onClick={() => saveEdit(prop.id)} className="h-8 px-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50"><Save className="w-4 h-4" /></Button>
                           <Button variant="ghost" size="sm" onClick={() => setEditingPropId(null)} className="h-8 px-2"><XCircle className="w-4 h-4" /></Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-1">
                           <Button variant="ghost" size="sm" onClick={() => startEditing(prop)} className="h-8 px-2 text-primary"><Pencil className="w-4 h-4" /></Button>
                           <Button variant="ghost" size="sm" onClick={() => deleteProposition(prop.id)} className="text-destructive h-8 px-2"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'moderation' && (
        <div className="bg-card rounded-xl shadow-sm border p-4 space-y-4">
          <h2 className="font-semibold text-lg">Soumissions des Partis</h2>
          <p className="text-sm text-muted-foreground">Validez ou rejetez les idées soumises par les délégués des partis politiques.</p>
          {pendingProps.length === 0 ? (
            <div className="p-8 text-center bg-muted/20 rounded-lg text-muted-foreground">Aucune proposition en attente.</div>
          ) : (
            <div className="grid gap-3">
              {pendingProps.map(prop => (
                <div key={prop.id} className="border rounded-lg p-4 flex items-start justify-between bg-background">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold">{prop.theme}</span>
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Proposé par : {prop.submittedBy}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">"{prop.textFr}"</p>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>Mots-clés : {prop.keywords.join(', ')}</span>
                      <span>•</span>
                      <span>Partis liés : {prop.partyIds.join(', ')}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => { updatePropositionStatus(prop.id, 'valide'); toast({ title: "Validé" }); }}><CheckCircle className="w-4 h-4 mr-1"/> Accepter</Button>
                    <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/10" onClick={() => { updatePropositionStatus(prop.id, 'rejete'); toast({ title: "Rejeté" }); }}><XCircle className="w-4 h-4 mr-1"/> Refuser</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div className="bg-card rounded-xl shadow-sm border p-6 max-w-2xl space-y-6">
          <div>
            <h2 className="font-semibold text-lg mb-1">Apparence de l'accroche (CTA)</h2>
            <p className="text-sm text-muted-foreground">Configurez comment le Match ton Parti est mis en avant sur la page d'accueil.</p>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-sm">Paramètres du jeu (Cartes affichées)</h3>
            <div className="space-y-2">
              <Label>Nombre de cartes par session (Min 5, Max 20)</Label>
              <Input type="number" min={5} max={20} value={itemsPerSession} onChange={e => updateItemsPerSession(parseInt(e.target.value) || 5)} />
              <p className="text-xs text-muted-foreground">À chaque fois qu'un utilisateur joue, ce nombre exact de cartes sera tiré de la base. Si l'utilisateur rejoue, 2 ou 3 cartes resteront les mêmes et les autres seront nouvelles, pour l'effet de surprise.</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div className="space-y-2">
              <Label>Type d'affichage</Label>
              <Select value={bannerConfig.mode} onValueChange={(v: 'banner'|'button') => updateBannerConfig({ mode: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="banner">Bannière pleine largeur</SelectItem>
                  <SelectItem value="button">Bouton flottant (Widget)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Emplacement (Si bouton flottant)</Label>
              <Select value={bannerConfig.placement} onValueChange={(v: 'top'|'bottom') => updateBannerConfig({ placement: v })} disabled={bannerConfig.mode === 'banner'}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bottom">En bas à droite</SelectItem>
                  <SelectItem value="top">En haut</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-sm">Textes d'accroche (FR/AR)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Texte principal (FR)</Label>
                <Input value={bannerConfig.textFr} onChange={e => updateBannerConfig({ textFr: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Texte principal (AR)</Label>
                <Input value={bannerConfig.textAr} onChange={e => updateBannerConfig({ textAr: e.target.value })} dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label>Texte du bouton (FR)</Label>
                <Input value={bannerConfig.buttonTextFr} onChange={e => updateBannerConfig({ buttonTextFr: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Texte du bouton (AR)</Label>
                <Input value={bannerConfig.buttonTextAr} onChange={e => updateBannerConfig({ buttonTextAr: e.target.value })} dir="rtl" />
              </div>
            </div>
            <Button onClick={() => toast({ title: "Configuration sauvegardée" })}>Enregistrer le design</Button>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-semibold text-sm">Mention de précaution (Page de résultats)</h3>
            <p className="text-xs text-muted-foreground">Cette phrase s'affiche en tout petit en bas de la page du podium pour rappeler aux citoyens et aux partis que ce module est un outil d'aide à la découverte, dont les données évoluent.</p>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Phrase de précaution (FR)</Label>
                <textarea 
                  className="w-full text-sm p-3 border rounded-md focus:ring-2 focus:ring-primary/20 outline-none" 
                  rows={2} 
                  value={discFr} 
                  onChange={e => setDiscFr(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label>Phrase de précaution (AR)</Label>
                <textarea 
                  className="w-full text-sm p-3 border rounded-md focus:ring-2 focus:ring-primary/20 outline-none text-right font-arabic" 
                  rows={2} 
                  dir="rtl"
                  value={discAr} 
                  onChange={e => setDiscAr(e.target.value)} 
                />
              </div>
            </div>
            <Button onClick={() => { updateDisclaimer(discFr, discAr); toast({ title: "Mention sauvegardée et publiée !" }); }}>
              <Check className="w-4 h-4 mr-2" /> Enregistrer et publier
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card rounded-xl border p-4 shadow-sm text-center">
              <p className="text-sm text-muted-foreground font-semibold">Participations Totales</p>
              <p className="text-4xl font-black font-display text-primary mt-2">{analytics.totalPlays}</p>
            </div>
            {/* Can add more cards here */}
          </div>
          
          <div className="bg-card rounded-xl border p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-lg">Classement des Partis (Apparition dans le Top 3)</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  if (confirm("Êtes-vous sûr de vouloir remettre tous les compteurs à zéro ?")) {
                    resetAnalytics();
                    toast({ title: "Compteurs réinitialisés" });
                  }
                }} className="text-destructive hover:bg-destructive/10">Réinitialiser les stats</Button>
                <Button variant="outline" size="sm">Exporter (.xlsx)</Button>
              </div>
            </div>
            <div className="space-y-3">
              {Object.entries(analytics.podiumCounts).sort((a, b) => b[1] - a[1]).map(([party, count], i) => (
                <div key={party} className="flex items-center gap-4">
                  <div className="w-6 font-bold text-muted-foreground text-right">{i+1}.</div>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-bold">{party}</span>
                      <span className="text-muted-foreground">{count} fois</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div className="bg-primary h-2 rounded-full" style={{ width: `${Math.min(100, (count / analytics.totalPlays) * 100)}%` }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
